/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: AI识别记录列表管理Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-20 16:47:07
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { message, Modal } from 'antd'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import {
  fetchAIRecognitionList,
  exportAIRecognitionExcel,
  editAIRecognition
} from '../../../actions/aiRecognition'
import {
  useAppDispatch,
  useAppSelector,
  useVip,
  usePayment
} from '../../../app/hooks'
import {
  RecognitionRecord,
  XC_RECOGNITION_STATUS,
  XCFilterParams
} from '../types/contextInterface'
import { useSensorTracking } from './useSensorTracking'
import { usePollAndDownload } from './usePollAndDownload'
import CONFIG from '../../../constants/config'

// 分页大小常量
const DEFAULT_PAGE_SIZE = 10

interface UseRecordsTableListOptions {
  initialPageSize?: number
}

interface UseRecordsTableListReturn {
  // 数据状态
  recognitionRecords: RecognitionRecord[]
  currentPage: number
  currentPageSize: number
  totalRecords: number
  loading: boolean
  dateRange: [Dayjs | null, Dayjs | null] | null
  saving: boolean
  exportingAll: boolean
  exportingSelected: boolean

  // 事件处理函数
  handleFetchRecords: (
    pageNo: number,
    pageSize?: number,
    filterParams?: any
  ) => Promise<void>
  handlePageChange: (page: number) => void
  handlePageSizeChange: (current: number, size: number) => void
  handleDownloadExcel: (filterParams: XCFilterParams) => Promise<void>
  setDateRange: (range: [Dayjs | null, Dayjs | null] | null) => void
  addRecord: (record: RecognitionRecord) => void
  updateRecord: (index: number, record: RecognitionRecord) => void
  handleSaveRecord: (
    record: RecognitionRecord,
    rowIndex: number,
    colIndex?: number
  ) => Promise<void>
  handleDeleteRecord: (
    record: RecognitionRecord,
    isLast: boolean,
    reportAction?: (params: any) => void,
    generateTrackingParams?: (record: RecognitionRecord) => any
  ) => Promise<{ success: boolean; error?: string }>
  handleBatchDeleteRecords: (
    records: RecognitionRecord[],
    reportAction?: (params: any) => void,
    generateTrackingParams?: (record: RecognitionRecord) => any
  ) => Promise<{ successCount: number; failedCount: number; errors: string[] }>
}

/**
 * AI识别记录列表管理Hook
 * 封装了记录获取、分页处理、Excel导出等功能
 */
export const useRecordsTableList = (
  options: UseRecordsTableListOptions = {}
): UseRecordsTableListReturn => {
  const { initialPageSize = DEFAULT_PAGE_SIZE } = options
  const dispatch = useAppDispatch()
  const {
    reportAISafetyHazardsHistoryClick,
    reportAISecurityClickFilter,
    reportAISecurityPageActionClick
  } = useSensorTracking()

  // 从Redux store获取用户和团队信息
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const groupId = useAppSelector((state) => state.group.groupId)

  // 会员与支付弹窗 Hook
  const { isVip } = useVip()
  const { open } = usePayment()

  // 状态管理
  const [recognitionRecords, setRecognitionRecords] = useState<
    RecognitionRecord[]
  >([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(initialPageSize)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [isInitialLoaded, setIsInitialLoaded] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [exportingAll, setExportingAll] = useState<boolean>(false)
  const [exportingSelected, setExportingSelected] = useState<boolean>(false)
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null)

  /**
   * 获取识别记录数据
   */
  const handleFetchRecords = useCallback(
    async (page = 1, size = currentPageSize, filterParams?: XCFilterParams) => {
      setLoading(true)

      // 构建请求参数
      const params: any = {
        groupID: groupId,
        pageNo: page,
        pageSize: size
      }

      // 如果传入了筛选参数，优先使用筛选参数
      if (filterParams) {
        if (filterParams.keywords) params.keywords = filterParams.keywords
        if (
          filterParams.questionTypeList &&
          filterParams.questionTypeList.length > 0
        )
          params.questionTypeList = filterParams.questionTypeList
        if (filterParams.monomerName)
          params.monomerName = filterParams.monomerName
        if (
          filterParams.departmentUsers &&
          filterParams.departmentUsers.length > 0
        )
          params.userIDList = filterParams.departmentUsers.map((user: any) =>
            typeof user === 'string' ? user : user.userID
          )
        if (filterParams.startTime) params.startTime = filterParams.startTime
        if (filterParams.endTime) params.endTime = filterParams.endTime
      }
      // 否则使用日期范围
      else if (dateRange && dateRange[0] && dateRange[1]) {
        params.startTime = dateRange[0].valueOf()
        params.endTime = dateRange[1].valueOf()
      }

      try {
        const result = await dispatch(fetchAIRecognitionList(params))

        // 处理识别记录数据，为没有整改期限的记录设置默认值
        const processedRecords = (result.list || []).map(
          (record: RecognitionRecord) => {
            // 如果没有整改期限，则设置为检测时间+3天
            if (!record.deadline && record.createTime) {
              const createTime = record.createTime
              const deadlineDate = dayjs(createTime).add(3, 'days')
              record.deadline = deadlineDate.format('YYYY-MM-DD')
            }
            record.status = XC_RECOGNITION_STATUS.COMPLETED
            return record
          }
        )

        setRecognitionRecords(processedRecords)
        setTotalRecords(result.total || 0)
        setCurrentPage(page)
        setLoading(false)
        setIsInitialLoaded(true)

        // 如果是筛选操作（传入了筛选参数），上报筛选埋点
        if (filterParams) {
          // 构建埋点参数
          const trackingParams = {
            startTime: dayjs(filterParams.startTime).format(CONFIG.timeFormat),
            endTime: dayjs(filterParams.endTime).format(CONFIG.timeFormat),
            memberNum: filterParams.departmentUsers?.length || 0,
            isAllMember:
              !filterParams.departmentUsers ||
              filterParams.departmentUsers.length === 0,
            infoJson: JSON.stringify({
              keywords: filterParams.keywords || '',
              monomerName: filterParams.monomerName || '',
              questionTypeList: filterParams.questionTypeList || []
            }),
            actionType: 'searchResult' as const,
            resultNum: processedRecords.length
          }

          // 上报筛选结果埋点
          reportAISecurityClickFilter(trackingParams)
        }
      } catch (error) {
        console.error('获取识别记录失败:', error)
        message.error('获取识别记录失败')
        setLoading(false)
        setIsInitialLoaded(true)
      }
    },
    [dispatch, groupId, dateRange, currentPageSize]
  )

  /**
   * 处理页码变化
   */
  const handlePageChange = useCallback(
    (page: number) => {
      handleFetchRecords(page, currentPageSize)
    },
    [handleFetchRecords, currentPageSize]
  )

  /**
   * 处理页面大小变化
   */
  const handlePageSizeChange = useCallback(
    (current: number, size: number) => {
      setCurrentPageSize(size)
      setCurrentPage(1)
      handleFetchRecords(1, size)
    },
    [handleFetchRecords]
  )

  const { pollAndDownload } = usePollAndDownload()

  /**
   * 处理下载Excel
   */
  const handleDownloadExcel = useCallback(
    async (filterParams: XCFilterParams) => {
      const isExportingAll = filterParams?.isAll
      const currentExportingState = isExportingAll
        ? exportingAll
        : exportingSelected

      // 防止重复点击
      if (currentExportingState) {
        message.warning('正在生成报表中，请稍候...')
        return
      }

      // 非会员用户点击导出，统一在 Hook 内处理开会员引导
      if (!isVip) {
        open('ai-security')
        return
      }

      if (recognitionRecords.length === 0) {
        message.warning('暂无数据可导出')
        return
      }

      // 上报导出表格埋点（页面级）
      reportAISecurityPageActionClick({
        actionType: 'downloadExcel',
        historyRecordNum: totalRecords
      })

      // 根据导出类型设置对应的状态
      if (isExportingAll) {
        setExportingAll(true)
      } else {
        setExportingSelected(true)
      }

      try {
        const exportParams = {
          groupID: groupId,
          userID: userId || '',
          format: 'excel',
          ids: filterParams?.isAll
            ? []
            : getTargetRecords(recognitionRecords).recordIds,
          monomerName: filterParams?.monomerName || '',
          questionTypeList: filterParams?.questionTypeList || [],
          userIDList: (filterParams?.departmentUsers || []).map((user: any) =>
            typeof user === 'string' ? user : user.userID
          ),
          keywords: filterParams?.keywords || ''
        }

        const result = (await dispatch(
          exportAIRecognitionExcel(exportParams)
        )) as any

        if (result.exportURL) {
          // 使用通用的 download 方法
          pollAndDownload({
            exportURL: result.exportURL,
            fileName: result.fileName || 'recognition_records_list.xlsx',
            defaultFileName: 'recognition_records_list.xlsx',
            onDownloadSuccess: (params) => {
              reportAISecurityPageActionClick({
                actionType: 'downloadExcelSuccess',
                ...params
              })
            }
          })
            .then(() => {
              message.success('文件下载成功')
            })
            .catch((error) => {
              console.error('下载失败:', error)
              message.error('下载失败，请稍后重试')
            })
            .finally(() => {
              // 根据导出类型重置对应的状态
              if (isExportingAll) {
                setExportingAll(false)
              } else {
                setExportingSelected(false)
              }
            })
        } else {
          throw new Error('导出失败')
        }
      } catch (error) {
        console.error('Excel导出错误:', error)
        message.error('导出失败，请重试')
        // 根据导出类型重置对应的状态
        if (isExportingAll) {
          setExportingAll(false)
        } else {
          setExportingSelected(false)
        }
      }
    },
    [
      dispatch,
      recognitionRecords,
      groupId,
      userId,
      totalRecords,
      pollAndDownload,
      reportAISecurityPageActionClick,
      exportingAll,
      exportingSelected,
      isVip,
      open
    ]
  )

  // 组件挂载时获取数据
  useEffect(() => {
    handleFetchRecords()
  }, [handleFetchRecords])

  /**
   * 添加新记录到列表开头
   */
  const addRecord = useCallback((record: RecognitionRecord) => {
    // 为新记录添加动画标识
    const recordWithAnimation = {
      ...record,
      isNewRecord: true
    }

    setRecognitionRecords((prevRecords) => [
      recordWithAnimation,
      ...prevRecords
    ])
    setTotalRecords((prevTotal) => prevTotal + 1)

    // 3秒后移除动画标识
    setTimeout(() => {
      setRecognitionRecords((currentRecords) =>
        currentRecords.map((r) =>
          (r.id !== undefined &&
            record.id !== undefined &&
            r.id === record.id) ||
          (r.uuid !== undefined &&
            record.uuid !== undefined &&
            r.uuid === record.uuid)
            ? { ...r, isNewRecord: false }
            : r
        )
      )
    }, 3000)
  }, [])

  /**
   * 更新列表中的记录
   */
  const updateRecord = useCallback(
    (index: number, record: RecognitionRecord) => {
      setRecognitionRecords((prevRecords) => {
        const newRecords = [...prevRecords]
        newRecords[index - 1] = record
        return newRecords
      })
    },
    []
  )

  // 公共工具函数：日期格式化与列表归一化，避免保存/删除重复逻辑
  const formatDate = useCallback((value?: string | number | Date | null) => {
    if (!value) return ''
    const m = dayjs(value)
    return m.isValid() ? m.format('YYYY-MM-DD') : ''
  }, [])

  const normalizeStringList = useCallback((value: any): string[] => {
    if (typeof value === 'string') {
      return value.split('\n').filter((item) => item.trim() !== '')
    }
    return Array.isArray(value) ? value : []
  }, [])

  /**
   * 获取要操作的记录列表（优先选中记录，否则使用全部记录）
   * @param allRecords 全部记录列表
   * @returns 要操作的记录列表和对应的ID列表
   */
  const getTargetRecords = useCallback((allRecords: RecognitionRecord[]) => {
    const selectedRecords = allRecords.filter((r) => r.isSelected)
    const targetRecords =
      selectedRecords.length > 0 ? selectedRecords : allRecords
    const recordIds = targetRecords
      .map((record) => record.id)
      .filter((id): id is number => id !== undefined)

    return {
      records: targetRecords,
      recordIds,
      isSelectedMode: selectedRecords.length > 0
    }
  }, [])

  const buildUpdateData = useCallback(
    (record: RecognitionRecord, isDeleteFlag: boolean) => {
      return {
        id: record.id || 0,
        monomerName: record.monomerName || '',
        RectificationManager: record.RectificationManager || '',
        ConstructioUnit: record.ConstructioUnit || '',
        RectificationMeasure: record.RectificationMeasure || '',
        questionType: record.questionType || '',
        deadline: formatDate((record as any).deadline),
        finishTime: formatDate((record as any).finishTime),
        comment: record.comment || '',
        groupID: groupId || '',
        photoFilesAfter: record.photoFilesAfter || [],
        potentialHazardList: normalizeStringList(
          (record as any).potentialHazardList
        ),
        standardList: normalizeStringList((record as any).standardList),
        status: record.status,
        isDelete: isDeleteFlag,
        projectName: record.projectName ?? '',
        monomerNameContent: (record as any).monomerNameContent ?? '',
        questionTypeContent: (record as any).questionTypeContent ?? '',
        potentialHazardListContent:
          (record as any).potentialHazardListContent ?? [],
        standardListContent: (record as any).standardListContent ?? [],
        monitorType: (() => {
          const hazardSourceArray = (record as any).hazardSourceList || []
          const selectedIndex = (record as any).selectedHazardSource ?? 0
          const selectedItem = hazardSourceArray[selectedIndex]
          return selectedItem ? selectedItem.val : 0
        })(),
        hazardSourceList: (record as any).hazardSourceList ?? []
      }
    },
    [groupId, formatDate, normalizeStringList]
  )

  /**
   * 保存编辑的记录数据
   * @param record - 要保存的记录
   * @param rowIndex - 记录在数据源中的行索引
   * @param colIndex - 列索引（可选）
   */
  const handleSaveRecord = useCallback(
    async (record: RecognitionRecord, rowIndex: number) => {
      setSaving(true)

      try {
        const updateData = buildUpdateData(record, !!(record as any).isDelete)

        // 保存数据
        const response = await dispatch(editAIRecognition(updateData))
        if ((response as any).code === 200) {
          if (updateData.isDelete) {
            // 删除本地数据
            setRecognitionRecords((prevRecords) =>
              prevRecords.filter((r) => r.id !== record.id)
            )
            setTotalRecords((prevTotal) => Math.max(prevTotal - 1, 0))
            message.success('删除成功')
          } else {
            // 更新本地数据
            message.success('保存成功')
            updateRecord(rowIndex, {
              ...record,
              status: XC_RECOGNITION_STATUS.COMPLETED
            })
          }
        } else {
          message.error(((response as any).msg as string) || '操作失败')
          throw new Error('保存失败')
        }
      } catch (error) {
        console.error('保存记录失败:', error)
        message.error('保存失败，请重试')
      } finally {
        setSaving(false)
      }
    },
    [dispatch, buildUpdateData, updateRecord]
  )

  /**
   * 删除记录（异步版本）
   */
  const handleDeleteRecord = useCallback(
    async (
      record: RecognitionRecord,
      isLast: boolean,
      reportAction?: (params: any) => void,
      generateTrackingParams?: (record: RecognitionRecord) => any
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        setSaving(true)

        const updateData = buildUpdateData(record, true)
        const response = await dispatch(editAIRecognition(updateData))

        if (response && (response as any).code === 200) {
          // 删除本地数据
          setRecognitionRecords((prevRecords) =>
            prevRecords.filter((r) => r.id !== record.id)
          )
          setTotalRecords((prevTotal) => Math.max(prevTotal - 1, 0))

          // 上报删除埋点
          if (reportAction) {
            reportAction({
              actionType: 'deleteRecord',
              ...(generateTrackingParams ? generateTrackingParams(record) : {})
            })
          }

          isLast && message.success('已删除选中记录')
          return { success: true }
        } else {
          const errorMsg = ((response as any)?.msg as string) || '删除失败'
          !isLast && message.error(`删除id: ${record.id} 失败: ${errorMsg}`)
          return { success: false, error: errorMsg }
        }
      } catch (error) {
        console.error('删除记录失败:', error)
        const errorMsg = '删除失败，请重试'
        !isLast && message.error(`删除id: ${record.id} 失败: ${errorMsg}`)
        return { success: false, error: errorMsg }
      } finally {
        setSaving(false)
      }
    },
    [buildUpdateData]
  )

  /**
   * 批量删除记录（异步顺序执行）
   */
  const handleBatchDeleteRecords = useCallback(
    async (
      records: RecognitionRecord[],
      reportAction?: (params: any) => void,
      generateTrackingParams?: (record: RecognitionRecord) => any
    ): Promise<{
      successCount: number
      failedCount: number
      errors: string[]
    }> => {
      const results = {
        successCount: 0,
        failedCount: 0,
        errors: [] as string[]
      }

      for (let i = 0; i < records.length; i++) {
        const record = records[i]
        const isLast = i === records.length - 1

        try {
          const result = await handleDeleteRecord(
            record,
            false, // 批量删除时不显示单个成功消息
            reportAction,
            generateTrackingParams
          )

          if (result.success) {
            results.successCount++
          } else {
            results.failedCount++
            results.errors.push(`记录 ${record.id}: ${result.error}`)
          }
        } catch (error) {
          results.failedCount++
          results.errors.push(`记录 ${record.id}: 删除异常`)
        }

        // 添加小延迟避免服务器压力过大
        if (!isLast) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      }

      return results
    },
    [handleDeleteRecord]
  )

  return {
    recognitionRecords,
    currentPage,
    currentPageSize,
    totalRecords,
    loading,
    dateRange,
    saving,
    exportingAll,
    exportingSelected,
    handleFetchRecords,
    handlePageChange,
    handlePageSizeChange,
    handleDownloadExcel,
    setDateRange,
    addRecord,
    updateRecord,
    handleSaveRecord,
    handleDeleteRecord,
    handleBatchDeleteRecords
  }
}

export default useRecordsTableList
